/**
 * <s-product-gallery> — sliding product gallery.
 *
 * Markup:
 *   [data-gallery-track]   horizontal scroll-snap track (native swipe follows the finger)
 *   [data-gallery-slide]   slides inside the track
 *   [data-gallery-prev] / [data-gallery-next] chevron buttons
 *   [data-gallery-thumb]   thumbnail buttons, same order as slides
 *   [data-gallery-bar]     hairline progress segments
 *   [data-gallery-status]  polite live region
 *
 * Buttons, thumbnails and arrow keys glide the track with an eased animation
 * (snap is paused while it runs). Reduced motion jumps instantly.
 */
class SProductGallery extends HTMLElement {
  static DURATION = 750;

  #ac;
  #index = 0;
  #frame = 0;
  #scrollTick = false;

  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    if (!this.track || this.slides.length < 2) return;

    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.bars = [...this.querySelectorAll('[data-gallery-bar]')];
    this.status = this.querySelector('[data-gallery-status]');

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.#index - 1), opts);
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.#index + 1), opts);
    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i), opts));
    this.track.addEventListener('scroll', this.#onScroll, { ...opts, passive: true });
    this.addEventListener('keydown', this.#onKeydown, opts);

    // Warm the remaining angles once idle so a glide never reveals a blank frame.
    const warm = () => this.track.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2500 });
    else setTimeout(warm, 1200);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  go(target) {
    const count = this.slides.length;
    const next = ((target % count) + count) % count;
    const slide = this.slides[next];
    slide.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));

    const delta = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    this.#setIndex(next);
    if (Math.abs(delta) < 1) return;

    cancelAnimationFrame(this.#frame);
    const start = this.track.scrollLeft;
    const end = start + delta;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.track.scrollLeft = end;
      return;
    }

    this.track.style.scrollSnapType = 'none';
    const began = performance.now();
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const step = (now) => {
      const t = Math.min(1, (now - began) / SProductGallery.DURATION);
      this.track.scrollLeft = start + delta * ease(t);
      if (t < 1) {
        this.#frame = requestAnimationFrame(step);
      } else {
        this.track.style.scrollSnapType = '';
      }
    };
    this.#frame = requestAnimationFrame(step);
  }

  #setIndex(index) {
    if (index === this.#index) return;
    this.#index = index;
    this.bars.forEach((bar, i) => bar.toggleAttribute('data-active', i === index));
    this.thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    if (this.status) this.status.textContent = `${index + 1} / ${this.slides.length}`;
  }

  // Keep the indicators in sync while the finger drags the track.
  #onScroll = () => {
    if (this.#scrollTick) return;
    this.#scrollTick = true;
    requestAnimationFrame(() => {
      this.#scrollTick = false;
      if (this.track.style.scrollSnapType === 'none') return;
      const trackLeft = this.track.getBoundingClientRect().left;
      let nearest = 0;
      let best = Infinity;
      this.slides.forEach((slide, i) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < best) {
          best = distance;
          nearest = i;
        }
      });
      this.#setIndex(nearest);
    });
  };

  #onKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    const forward = (event.key === 'ArrowRight') !== this.#isRtl;
    event.preventDefault();
    this.go(this.#index + (forward ? 1 : -1));
  };
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
